import React, { useRef, useEffect } from 'react';
import { Link } from "react-router-dom";
import Button from "../../components/atoms/Button";
import ButtonGroup from "../../components/molecules/ButtonGroup";
import InputGroup from "../../components/molecules/InputGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateRight } from "@fortawesome/free-solid-svg-icons";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from 'moment-timezone';

const GolfEventHeaderNav = ({
  locationData,
  loading,
  isToday,
  onRefresh,
  selectedDate,
  setSelectedDate,
  minDate,
  maxDate,
  enabledDates,
  showCalendar,
  setShowCalendar,
  searchTerm,
  setSearchTerm,
  filterType,
  setFilterType
}) => {
  const calendarRef = useRef(null);
  const tz = locationData?.timeZone || 'America/New_York';

  // Utility function to safely convert to Date object
  // Uses noon to prevent day-boundary shift when browser timezone differs from location timezone
  const toDate = (date) => {
    if (!date) return null;
    if (date instanceof Date) return date;
    if (typeof date === 'string' && tz) {
      return moment.tz(date + ' 12:00', 'YYYY-MM-DD HH:mm', tz).toDate();
    }
    return new Date(date);
  };

  const minDateObj = toDate(minDate);
  const maxDateObj = toDate(maxDate);
  const enabledDatesArray = enabledDates?.map(toDate) || [];

  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar);
  };

  const handleDateSelect = (date) => {
    // react-calendar returns a Date at midnight browser-local time
    // Extract the date as the user sees it in their browser, then recreate at noon in the location's timezone
    // This prevents day-boundary shift when browser timezone differs from location timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const normalizedDate = moment.tz(dateStr + ' 12:00', 'YYYY-MM-DD HH:mm', tz).toDate();

    let clampedDate = normalizedDate;
    if (minDateObj && normalizedDate < minDateObj) {
      clampedDate = minDateObj;
    } else if (maxDateObj && normalizedDate > maxDateObj) {
      clampedDate = maxDateObj;
    }
    setSelectedDate(clampedDate);
    setShowCalendar(false);
  };

  const tileDisabled = ({ date, view }) => {
    if (view !== 'month') return false;
    if (!enabledDatesArray || enabledDatesArray.length === 0) return false;
    const dateString = moment(date).format('YYYY-MM-DD');
    return !enabledDatesArray.some(enabledDate =>
      moment(enabledDate).format('YYYY-MM-DD') === dateString
    );
  };

  const handleClickOutside = (event) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      setShowCalendar(false);
    }
  };

  useEffect(() => {
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  const displayDate = selectedDate ? moment(selectedDate).format("l") : moment().format("l");
  const isSelectedToday = isToday;

  const getDefaultActiveStartDate = () => {
    if (selectedDate) {
      return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    }
    if (minDateObj) {
      return new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1);
    }
    return new Date();
  };

  const getNavigationLabel = ({ date }) => {
    if (minDateObj && maxDateObj) {
      const minMonth = minDateObj.getMonth();
      const maxMonth = maxDateObj.getMonth();
      const minYear = minDateObj.getFullYear();
      const maxYear = maxDateObj.getFullYear();
      if (minMonth === maxMonth && minYear === maxYear) {
        return `${moment(minDateObj).format('MMMM YYYY')}`;
      }
    }
    return moment(date).format('MMMM YYYY');
  };

  const shouldDisableNavigation = () => {
    if (minDateObj && maxDateObj) {
      const minMonth = minDateObj.getMonth();
      const maxMonth = maxDateObj.getMonth();
      const minYear = minDateObj.getFullYear();
      const maxYear = maxDateObj.getFullYear();
      return minMonth === maxMonth && minYear === maxYear;
    }
    return false;
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Top Row - Logo, title, calendar, refresh */}
      <div className="between">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img
            style={{
              marginRight: "24px",
              transform: 'scale(1.1)',
              borderRadius: "12px"
            }}
            width={80}
            height={80}
            src={require(`../../clients/${locationData.logoPath}`)}
            alt="logo"
          />
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span className="my-auto" style={{ fontSize: "2.5rem", lineHeight: '1.2' }}>
              {locationData.name}
            </span>
            {locationData.subName && (
              <span className="my-auto" style={{ fontSize: "1.5rem", color: '#e20074', marginTop: '4px' }}>
                {locationData.subName}
              </span>
            )}
          </div>
        </div>

        <div className="my-auto">
          <InputGroup>
            {isSelectedToday && (
              <div className="d-flex align-items-center">
                <span style={{ color: "red", marginRight: "12px", fontSize: "1.25rem" }}>
                  LIVE
                </span>
                <span className="pulsing-dot" style={{ marginRight: "16px" }}></span>
              </div>
            )}
            <ButtonGroup className="me-1">
              <Button
                className="panel-2 btn"
                icon="calendar"
                text={
                  displayDate +
                  (isSelectedToday ? " (Today)" : "")
                }
                onClick={handleCalendarToggle}
                disabled={loading}
              />
              <Button
                disabled={true}
                icon={"calendar-event"}
                text="Event Day"
                className={`panel-2 active`}
              />
            </ButtonGroup>
            <ButtonGroup>
              {isSelectedToday && (
                <Button
                  className="border-0"
                  onClick={onRefresh}
                  disabled={loading}>
                  {!loading && (
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faArrowRotateRight} />
                      <span style={{ marginLeft: "8px" }}>Refresh</span>
                    </div>
                  )}
                </Button>
              )}
            </ButtonGroup>
          </InputGroup>
        </div>
      </div>

      {/* Search and Filter Row */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "16px" }}>
        <input
          type="text"
          placeholder="Search locations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: 1,
            padding: "8px 14px",
            borderRadius: "8px",
            border: "1px solid #ddd",
            fontSize: "14px",
            outline: "none",
            maxWidth: "300px"
          }}
        />
        <div style={{ display: "flex", gap: "6px" }}>
          {["all", "grandstand", "market", "gate"].map(type => (
            <button
              key={type}
              className={`btn btn-sm ${filterType === type ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setFilterType(type)}
              style={{ textTransform: "capitalize", fontSize: "13px" }}
            >
              {type === "all" ? "All" : type + "s"}
            </button>
          ))}
        </div>
      </div>

      {/* Calendar Dropdown */}
      {showCalendar && (
        <div
          ref={calendarRef}
          style={{
            position: 'absolute',
            top: '80px',
            right: '5%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease-in-out',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            background: 'white',
            borderRadius: '8px',
          }}
        >
          <div className="calendar-container">
            <Calendar
              onChange={handleDateSelect}
              value={selectedDate}
              minDate={minDateObj}
              maxDate={maxDateObj}
              defaultActiveStartDate={getDefaultActiveStartDate()}
              minDetail="month"
              maxDetail="month"
              showNeighboringMonth={false}
              navigationLabel={getNavigationLabel}
              prev2Label={null}
              next2Label={null}
              prevLabel={shouldDisableNavigation() ? null : undefined}
              nextLabel={shouldDisableNavigation() ? null : undefined}
              tileDisabled={tileDisabled}
            />
          </div>
        </div>
      )}

      <style>
        {`
          .pulsing-dot {
            width: 12px;
            height: 12px;
            background-color: red;
            border-radius: 50%;
            animation: pulse 2s infinite;
          }
          
          @keyframes pulse {
            0% {
              transform: scale(0.95);
              box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7);
            }
            70% {
              transform: scale(1);
              box-shadow: 0 0 0 6px rgba(255, 0, 0, 0);
            }
            100% {
              transform: scale(0.95);
              box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
            }
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
            to { opacity: 1; transform: translateX(-50%) translateY(0); }
          }

          .react-calendar__tile--active {
            background: #53b8aa !important;
            color: black !important;
          }
          .react-calendar__tile--active:enabled:hover,
          .react-calendar__tile--active:enabled:focus {
            background: #53b8aa !important;
          }
          .react-calendar__tile--now {
            background: #9fdbd2 !important;
          }
          .react-calendar__tile--now:enabled:hover,
          .react-calendar__tile--now:enabled:focus {
            background: #9fdbd2 !important;
          }
          .react-calendar__tile:disabled {
            background-color: #f0f0f0 !important;
            color: #ccc !important;
            cursor: not-allowed !important;
          }
          .react-calendar__navigation button {
            display: ${shouldDisableNavigation() ? 'none' : 'flex'};
          }
          .react-calendar__navigation__label {
            display: flex !important;
            pointer-events: none;
            background: transparent !important;
            font-weight: bold;
          }
        `}
      </style>
    </div>
  );
};

export default GolfEventHeaderNav;